'use client'

import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react'
// Import the Grammarly handler
import '@/lib/handle-grammarly'

// Define the context properties
interface GrammarlyContextProps {
  isGrammarlyLoaded: boolean
}

// Create the context with a default value
const GrammarlyContext = createContext<GrammarlyContextProps>({
  isGrammarlyLoaded: false
})

// Hook to use the Grammarly context
export const useGrammarly = () => useContext(GrammarlyContext)

interface GrammarlyProviderProps {
  children: ReactNode
}

export function GrammarlyProvider({ children }: GrammarlyProviderProps) {
  const [isGrammarlyLoaded, setIsGrammarlyLoaded] = useState(false)

  useEffect(() => {
    // Check if Grammarly is present on the client side
    const hasGrammarlyAttributes =
      document.documentElement.hasAttribute('data-gr-ext-installed') ||
      document.body.hasAttribute('data-gr-ext-installed')

    setIsGrammarlyLoaded(hasGrammarlyAttributes)

    // Add event listener for Grammarly's initialization
    const handleGrammarlyLoad = () => {
      setIsGrammarlyLoaded(true)
    }

    window.addEventListener('grammarly:load', handleGrammarlyLoad)

    // Clean up
    return () => {
      window.removeEventListener('grammarly:load', handleGrammarlyLoad)
    }
  }, [])

  return (
    <GrammarlyContext.Provider value={{ isGrammarlyLoaded }}>{children}</GrammarlyContext.Provider>
  )
}
